/**
 * Uppladdningskö för inspelningsdelar. Ren logik, inget DOM/nätverk – hooken
 * `useInspelning` äger uppladdningen, kön äger ordning och bokföring.
 *
 * Regler:
 *  - `nasta()` ger lägsta delnummer som väntar och inte redan är på väg upp.
 *    Delen markeras då som "på väg" tills `klar` eller `misslyckad` anropas.
 *  - `misslyckad(nr)` lägger tillbaka delen; den kommer först igen i nummerordning
 *    (en misslyckad del 3 går före ny del 4). En del tappas aldrig.
 *  - `lagg(nr, …)` ignoreras om nr redan finns (väntar, på väg eller är klar):
 *    ingen dubblett, och en uppladdad del laddas inte upp en gång till.
 *  - `vantande()` = delar som ännu inte är bekräftat uppladdade (väntar + på väg).
 */
export interface KoDel {
  nr: number;
  blob: Blob;
}

export interface InspelningsKo {
  lagg(nr: number, blob: Blob): void;
  nasta(): KoDel | null;
  klar(nr: number): void;
  misslyckad(nr: number): void;
  vantande(): number;
}

export function skapaKo(): InspelningsKo {
  const vantar = new Map<number, Blob>();
  const paVag = new Map<number, Blob>();
  const klara = new Set<number>();

  return {
    lagg(nr, blob) {
      if (vantar.has(nr) || paVag.has(nr) || klara.has(nr)) return;
      vantar.set(nr, blob);
    },
    nasta() {
      let lagst: number | null = null;
      for (const nr of vantar.keys()) if (lagst === null || nr < lagst) lagst = nr;
      if (lagst === null) return null;
      const blob = vantar.get(lagst)!;
      vantar.delete(lagst);
      paVag.set(lagst, blob);
      return { nr: lagst, blob };
    },
    klar(nr) {
      if (!paVag.has(nr) && !vantar.has(nr)) return;
      paVag.delete(nr);
      vantar.delete(nr);
      klara.add(nr);
    },
    misslyckad(nr) {
      const blob = paVag.get(nr);
      if (!blob) return;
      paVag.delete(nr);
      vantar.set(nr, blob);
    },
    vantande() {
      return vantar.size + paVag.size;
    },
  };
}

/**
 * Sorterar ett uppladdningsfel: permanent (4xx utom 408/429 – att försöka igen
 * ger samma svar) eller övergående (nät, 5xx, 408, 429). Statusen läses ur
 * `status` eller `statusCode` (Supabase Storage skickar den som sträng).
 * `serverText` sätts av `motenApi` och är serverns svenska feltext; Storages
 * egna texter är engelska och visas aldrig.
 */
export interface UppladdningsFel {
  permanent: boolean;
  text: string;
}

export function klassaUppladdningsfel(e: unknown): UppladdningsFel {
  const o = (e && typeof e === 'object' ? e : {}) as Record<string, unknown>;
  const rå = o.status ?? o.statusCode;
  const status = typeof rå === 'number' ? rå : typeof rå === 'string' ? Number(rå) : NaN;
  // 401 = utgången session mitt i en lång inspelning; klienten förnyar token, så delen
  // stannar i kön och försöks igen i stället för att stoppa mikrofonen.
  if (Number.isInteger(status) && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429) {
    const server = typeof o.serverText === 'string' ? o.serverText.trim() : '';
    return {
      permanent: true,
      text: `Uppladdningen nekades av servern – kontakta support.${server ? ` ${server}` : ''}`,
    };
  }
  if (Number.isInteger(status)) return { permanent: false, text: 'Servern svarade inte som väntat – försöker igen.' };
  return { permanent: false, text: 'Nätverksfel – försöker igen.' };
}
